import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Button } from '@deephaven/components';
import type { dh as DhType } from '@deephaven/jsapi-types';
import Log from '@deephaven/log';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { vsWarning } from '@deephaven/icons';
import { FormatColumnWhereIcon, FormatRowWhereIcon } from '../icons';
import MultiColumnFormatEditor from './MultiColumnFormatEditor';
import RowFormatEditor from './RowFormatEditor';
import {
  type BaseFormatConfig,
  FormatterType,
  type FormattingRule,
  isSupportedColumn,
  type ModelColumn,
} from './ConditionalFormattingUtils';
import './ConditionalFormatEditor.scss';

const log = Log.module('ConditionalFormatEditor');

export type SaveCallback = (rule: FormattingRule) => void;

export type UpdateCallback = (rule?: FormattingRule) => void;

export type CancelCallback = () => void;

export interface ConditionalFormatEditorProps {
  dh: typeof DhType;
  columns: readonly ModelColumn[];
  rule?: FormattingRule;
  errorMessage?: string;
  onCancel?: CancelCallback;
  onSave?: SaveCallback;
  onUpdate?: UpdateCallback;
}

const DEFAULT_CALLBACK = (): void => undefined;

function getFormatterTypeIcon(option: FormatterType): JSX.Element | undefined {
  switch (option) {
    case FormatterType.CONDITIONAL:
      return <FormatColumnWhereIcon />;
    case FormatterType.ROWS:
      return <FormatRowWhereIcon />;
  }
}

function getFormatterTypeLabel(option: FormatterType): string {
  switch (option) {
    case FormatterType.CONDITIONAL:
      return 'Conditional';
    case FormatterType.ROWS:
      return 'Rows';
  }
}

const formatterTypes = [FormatterType.CONDITIONAL, FormatterType.ROWS];

function ConditionalFormatEditor(
  props: ConditionalFormatEditorProps
): JSX.Element {
  const {
    columns: originalColumns,
    dh,
    errorMessage,
    onSave = DEFAULT_CALLBACK,
    onUpdate = DEFAULT_CALLBACK,
    onCancel = DEFAULT_CALLBACK,
    rule: defaultRule,
  } = props;

  const columns = originalColumns.filter(isSupportedColumn);

  const [selectedFormatter, setFormatter] = useState(
    defaultRule?.type ?? formatterTypes[0]
  );
  const [rule, setRule] = useState(defaultRule);
  const [isValid, setIsValid] = useState(false);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleSave = useCallback(() => {
    if (rule === undefined) {
      log.error('Rule is not defined.');
      return;
    }
    onSave(rule);
  }, [onSave, rule]);

  const handleFormatterChange = useCallback((value: FormatterType) => {
    log.debug('handleFormatterChange', value);
    setFormatter(value);
  }, []);

  const handleRuleChange = useCallback(
    (ruleConfig: BaseFormatConfig, isRuleValid: boolean) => {
      log.debug('handleRuleChange', ruleConfig, isRuleValid, selectedFormatter);
      const updatedRule = {
        type: selectedFormatter,
        config: ruleConfig,
      };
      setRule(updatedRule);
      setIsValid(isRuleValid);
      onUpdate(isRuleValid ? updatedRule : undefined);
    },
    [onUpdate, selectedFormatter]
  );

  return (
    <div className="conditional-format-editor form">
      <div className="mb-2">
        <label className="mb-0" htmlFor="formatter-select">
          Select Formatter
        </label>

        <div className="formatter-list">
          {formatterTypes.map((type, index) => (
            <div key={type} className="formatter-type">
              <button
                type="button"
                className={classNames('btn', 'btn-icon', 'btn-formatter-type', {
                  active: type === selectedFormatter,
                })}
                data-index={index}
                onClick={() => handleFormatterChange(type)}
              >
                {getFormatterTypeIcon(type)}
                {getFormatterTypeLabel(type)}
              </button>
            </div>
          ))}
        </div>
      </div>
      {selectedFormatter === FormatterType.CONDITIONAL && (
        <MultiColumnFormatEditor
          columns={columns}
          dh={dh}
          config={rule?.config}
          onChange={handleRuleChange}
        />
      )}
      {selectedFormatter === FormatterType.ROWS && (
        <RowFormatEditor
          columns={columns}
          config={rule?.config}
          dh={dh}
          onChange={handleRuleChange}
        />
      )}
      <hr />
      {errorMessage != null && errorMessage !== '' && (
        <div className="error-message">
          <p>
            <FontAwesomeIcon icon={vsWarning} /> Failed to preview conditional
            formatting.
          </p>
          <div className="error-box">{errorMessage}</div>
        </div>
      )}
      <div className="d-flex justify-content-end my-3">
        <Button kind="secondary" onClick={handleCancel} className="mr-2">
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={handleSave}
          disabled={rule === undefined || !isValid || errorMessage != null}
        >
          Done
        </Button>
      </div>
    </div>
  );
}

export default ConditionalFormatEditor;
